import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import Popup from "../../../components/Popup";
import "../../css/List.css";
import "../../css/TransactionsHistory.css";
import PendingTransactionsController from "../../../controller/Transaction/pendingTransactionsController";
import { TransactionType, TransactionStatus } from "../../../utils/enums";
import TransactionDetailsController from "../../../controller/Transaction/transactionDetailsController";
import TransactionDetailsModal from "../../../components/TransactionDetailsModal";
import { parseLocalDateFromYmd } from "../../../utils/dateRangeApiFormat";
import {
  MFG_FCM_NAV_TARGET_KEY,
  MFG_FCM_NAV_TX_KEY,
} from "../../../services/fcmNotificationHandler";

const PendingTransactions = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const [user, setUser] = useState(null);
  const [typeFilter, setTypeFilter] = useState(TransactionType.ALL);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [pagination, setPagination] = useState({ page: 0, perPage: 10, total: 0, lastPage: 0 });
  const [detailsTx, setDetailsTx] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    dateFrom: null,
    dateTo: null,
    presetId: null,
  });

  const transactionTypeLabels = {
    [TransactionType.INTERNAL_TRANSFER]: t("internal_transfer", { ns: "mobile" }),
    [TransactionType.ADMIN_FEES]: t("admin_fees", { ns: "mobile" }),
    [TransactionType.GENERATE]: t("generate", { ns: "mobile" }),
    [TransactionType.DEPOSIT]: t("deposit", { ns: "mobile" }),
    [TransactionType.WITHDRAWAL]: t("withdrawal", { ns: "mobile" }),
    [TransactionType.CASHIN]: t("cashin", { ns: "mobile" }),
    [TransactionType.CASHOUT]: t("cashout", { ns: "mobile" }),
    [TransactionType.COMMISSION]: t("commission", { ns: "mobile" }),
    [TransactionType.CREDIT_IN]: t("credit_in", { ns: "mobile" }),
    [TransactionType.CREDIT_OUT]: t("credit_out", { ns: "mobile" }),
    [TransactionType.EXCHANGE]: t("exchange", { ns: "mobile" }),
    [TransactionType.REFUND]: t("refund", { ns: "mobile" }),
    [TransactionType.PAY_BY_CARD]: t("pay_with_card", { ns: "mobile" }),
    [TransactionType.RESET_WALLET]: t("reset_wallet", { ns: "mobile" }),
    [TransactionType.RESET_BALANCE_MT5]: t("reset_mt5_balance", { ns: "mobile" }),
    [TransactionType.COLLECTED]: t("collected", { ns: "mobile" }),
    [TransactionType.REFUND_COLLECTED]: t("refund_collect", { ns: "mobile" }),
    [TransactionType.TRANSFER]: t("transfer", { ns: "mobile" }),
    [TransactionType.CASHIN_MT5]: t("cashin", { ns: "mobile" }),
    [TransactionType.BALANCE_REPORT]: t("balance", { ns: "mobile" }),
  };

  const transactionStatusLabels = {
    [TransactionStatus.STARTED]: t("started", { ns: "mobile" }),
    [TransactionStatus.PENDING]: t("pending", { ns: "mobile" }),
    [TransactionStatus.FAILED]: t("failed", { ns: "mobile" }),
    [TransactionStatus.COMPLETED]: t("completed", { ns: "mobile" }),
    [TransactionStatus.REJECTED]: t("rejected", { ns: "mobile" }),
    [TransactionStatus.CANCELED]: t("canceled", { ns: "mobile" }),
    [TransactionStatus.PROGRESSING]: t("waiting", { ns: "mobile" }),
    [TransactionStatus.PENDING_USER]: t("pending", { ns: "mobile" }),
    [TransactionStatus.COLLECTED]: t("collected", { ns: "mobile" }),
    [TransactionStatus.TRANSFERRED]: t("transferred", { ns: "mobile" }),
  };

  const mapTransactionWordLabel = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;
    const normalized = raw.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
    if (normalized === "cash in" || normalized === "cashin") return t("cashin", { ns: "mobile" });
    if (normalized === "cash out" || normalized === "cashout") return t("cashout", { ns: "mobile" });
    if (normalized === "deposit") return t("deposit", { ns: "mobile" });
    if (normalized === "withdraw" || normalized === "withdrawal") return t("withdrawal", { ns: "mobile" });
    if (normalized === "internal transfer" || normalized === "internaltransfer") {
      return t("internal_transfer", { ns: "mobile" });
    }
    return null;
  };

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData") || localStorage.getItem("userData");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (_) {
      setUser(null);
    }
  }, []);

  const getStatusClass = (statusCode) => {
    switch (statusCode) {
      case TransactionStatus.PENDING:
      case TransactionStatus.PENDING_USER:
      case TransactionStatus.STARTED:
      case TransactionStatus.PROGRESSING:
        return "status-pending";
      case TransactionStatus.COMPLETED:
      case TransactionStatus.COLLECTED:
      case TransactionStatus.TRANSFERRED:
        return "status-completed";
      case TransactionStatus.FAILED:
      case TransactionStatus.REJECTED:
      case TransactionStatus.CANCELED:
        return "status-failed";
      default:
        return "status-completed";
    }
  };

  const loadTransactions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await PendingTransactionsController.getPendingTransactions({
        page,
        perPage,
        type: typeFilter,
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        search: searchTerm || null,
        status: 1,
      });

      const rawDataBlock = result?.raw?.DATA || {};
      const rawList = Array.isArray(rawDataBlock.data) ? rawDataBlock.data : [];

      const sourceList =
        result.items && result.items.length ? result.items : rawList;

      const normalized = sourceList.map((item) => {
        const rawItem = item.raw || item;

        const id =
          rawItem.TRANSACTION_ID ||
          rawItem.REF_ID ||
          rawItem.REFID ||
          rawItem.REFERENCE_ID ||
          rawItem.ID ||
          rawItem.id ||
          null;

        const date =
          rawItem.TRANSACTION_DATE ||
          rawItem.TRX_DATE ||
          rawItem.CREATED_AT ||
          rawItem.DATE ||
          rawItem.date ||
          null;

        const amount =
          rawItem.AMOUNT ??
          rawItem.amount ??
          rawItem.VALUE ??
          null;

        const balanceSnapshot =
          rawItem.BALANCE_SNAPSHOT ??
          rawItem.balanceSnapshot ??
          null;

        let balanceAfter =
          rawItem.BALANCE_AFTER ??
          rawItem.BALANCE ??
          rawItem.CURRENT_BALANCE ??
          rawItem.balance ??
          null;

        if (
          balanceAfter == null &&
          balanceSnapshot &&
          typeof balanceSnapshot === "object"
        ) {
          const storedUserData = sessionStorage.getItem("userData") || localStorage.getItem("userData");
          let currentUserId = null;
          try {
            if (storedUserData) {
              const parsedUser = JSON.parse(storedUserData);
              currentUserId = parsedUser?.id || parsedUser?.ID || parsedUser?.user?.id;
            }
          } catch (_) {}

          const possibleWalletIds = [];
          const senderWallet = rawItem.sender_wallet || rawItem.SENDER_WALLET;
          const receiverWallet = rawItem.receiver_wallet || rawItem.RECEIVER_WALLET;

          if (senderWallet?.account?.user?.id === currentUserId ||
              senderWallet?.account?.user?.ID === currentUserId) {
            possibleWalletIds.push(senderWallet.id || senderWallet.ID);
          }
          if (receiverWallet?.account?.user?.id === currentUserId ||
              receiverWallet?.account?.user?.ID === currentUserId) {
            possibleWalletIds.push(receiverWallet.id || receiverWallet.ID);
          }

          if (possibleWalletIds.length === 0) {
            possibleWalletIds.push(...Object.keys(balanceSnapshot));
          }

          for (const walletId of possibleWalletIds) {
            const balanceValue =
              balanceSnapshot[walletId] ??
              balanceSnapshot[String(walletId)] ??
              (typeof walletId === "string" ? balanceSnapshot[parseInt(walletId, 10)] : null);

            if (balanceValue != null) {
              balanceAfter = balanceValue;
              break;
            }
          }
        }

        const typeCode =
          rawItem.TYPE ??
          rawItem.type ??
          null;

        const rawStatus =
          rawItem.STATUS ??
          rawItem.status ??
          null;

        const statusCode =
          typeof rawStatus === "number" ? rawStatus : null;

        const statusLabelFromApi =
          rawItem.STATUS_LABEL ||
          rawItem.STATUS_NAME ||
          (typeof rawStatus === "string" ? rawStatus : null);

        const translatedStatusLabelFromApi = mapTransactionWordLabel(statusLabelFromApi);

        const statusLabel =
          translatedStatusLabelFromApi ||
          statusLabelFromApi ||
          (typeof statusCode === "number" && transactionStatusLabels[statusCode]) ||
          null;

        const typeLabelFromApi =
          rawItem.TYPE_LABEL ||
          rawItem.TYPE_NAME ||
          (typeof typeCode === "string" ? typeCode : null);
        const translatedTypeLabelFromApi = mapTransactionWordLabel(typeLabelFromApi);

        const typeLabel =
          (typeof typeCode === "number" && transactionTypeLabels[typeCode]) ||
          translatedTypeLabelFromApi ||
          typeLabelFromApi ||
          t("transaction", { ns: "mobile", defaultValue: "Transaction" });

        let typeClass = "type-unknown";
        if (typeCode === TransactionType.CASHIN || typeCode === TransactionType.CASHIN_MT5) {
          typeClass = "type-cashin";
        } else if (typeCode === TransactionType.CASHOUT) {
          typeClass = "type-cashout";
        } else if (typeCode === TransactionType.INTERNAL_TRANSFER || typeCode === TransactionType.TRANSFER) {
          typeClass = "type-transfer";
        } else {
          typeClass = "type-real";
        }

        return {
          id,
          date,
          amount,
          balanceAfter,
          statusLabel,
          statusCode,
          typeCode,
          typeLabel,
          typeClass,
          raw: rawItem,
        };
      });

      setTransactions(normalized);

      const currentPage = Number(rawDataBlock.current_page ?? (result.pagination?.page ?? 0) + 1);
      const lastPage = Number(rawDataBlock.last_page ?? (result.pagination?.lastPage ?? 0) + 1);
      const perPageVal = Number(rawDataBlock.per_page ?? result.pagination?.perPage ?? perPage) || perPage;
      const totalVal = Number(rawDataBlock.total ?? result.pagination?.total ?? 0);

      setPagination({
        page: currentPage - 1,
        perPage: perPageVal,
        total: totalVal,
        lastPage: lastPage - 1,
      });
    } catch (e) {
      setError(e.message || "Failed to load pending transactions");
      setTransactions([]);
      console.error("[PendingTransactions] Failed to load transactions", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, page]);

  const canGoPrev = page > 0;
  const canGoNext =
    pagination.lastPage != null
      ? page < Number(pagination.lastPage)
      : transactions.length === perPage;

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString();
    } catch (_) {
      return value;
    }
  };

  const resolveCurrencySymbol = (tx) => {
    if (!tx) return "";
    const raw = tx.raw || {};
    return (
      raw.CURRENCY_SYMBOL ||
      raw.currencySymbol ||
      (raw.CURRENCY && (raw.CURRENCY.CURRENCY_SYMBOL || raw.CURRENCY.SYMBOL || raw.CURRENCY.symbol)) ||
      (raw.currencies &&
        (raw.currencies.CURRENCY_SYMBOL || raw.currencies.SYMBOL || raw.currencies.symbol)) ||
      ""
    );
  };

  const handleSearch = () => {
    setPage(0);
    loadTransactions();
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const toDateInputValue = (v) => {
    if (!v) return "";
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = String(d.getFullYear());
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const openDetails = async (tx) => {
    if (!tx || !tx.id) {
      return;
    }
    setDetailsTx(tx);
    setDetailsData(null);
    setDetailsError(null);
    setIsDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const data = await TransactionDetailsController.getTransactionDetails(tx.id);
      setDetailsData(data);
    } catch (e) {
      setDetailsError(e.message || "Failed to load transaction details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setDetailsTx(null);
    setDetailsData(null);
    setDetailsError(null);
  };

  useEffect(() => {
    try {
      const target = sessionStorage.getItem(MFG_FCM_NAV_TARGET_KEY);
      const txId = sessionStorage.getItem(MFG_FCM_NAV_TX_KEY);
      if (target !== "pending" || !txId) return;
      sessionStorage.removeItem(MFG_FCM_NAV_TARGET_KEY);
      sessionStorage.removeItem(MFG_FCM_NAV_TX_KEY);
      openDetails({ id: txId, raw: {} });
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canCancelTransaction = (tx, details) => {
    const statusCode = details?.statusCode ?? tx?.statusCode;
    const typeCode = details?.typeCode ?? tx?.typeCode;
    if (statusCode !== TransactionStatus.PENDING) return false;
    return (
      typeCode === TransactionType.DEPOSIT ||
      typeCode === TransactionType.WITHDRAWAL
    );
  };

  const handleCancelTransaction = async () => {
    const txId = detailsData?.id || detailsTx?.id;
    if (!txId) return;

    setCancelLoading(true);
    try {
      await PendingTransactionsController.cancelPendingTransaction(txId);
      closeDetails();
      loadTransactions();
    } catch (e) {
      setDetailsError(e.message || "Failed to cancel transaction");
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="accounts-list-page transactions-centered-page transactions-history-page">
      <Sidebar user={user} />
      <div className="accounts-list-content">
        <div className="transactions-history-filters">
          <div className="transactions-filter-bar w-100" aria-label="Filter transactions">
            <div className="transactions-filter-row">
              <div className="transactions-filter-field transactions-filter-field--search">
                <label>{t("search", { ns: "mobile" })}</label>
                <div className="transactions-filter-input-wrap">
                  <i className="fa-solid fa-magnifying-glass" aria-hidden />
                  <input
                    type="text"
                    className="transactions-filter-input"
                    placeholder={t("search_by_id_name_status", { ns: "mobile" })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyPress}
                  />
                </div>
              </div>
              <div className="transactions-filter-field">
                <label>{t("type", { ns: "mobile" })}</label>
                <select
                  className="transactions-filter-select"
                  value={String(typeFilter)}
                  onChange={(e) => {
                    setTypeFilter(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  <option value={TransactionType.ALL}>{t("all", { ns: "mobile" })}</option>
                  <option value={TransactionType.DEPOSIT}>{t("deposit", { ns: "mobile" })}</option>
                  <option value={TransactionType.WITHDRAWAL}>{t("withdrawal", { ns: "mobile" })}</option>
                  <option value={TransactionType.INTERNAL_TRANSFER}>{t("internal_transfer", { ns: "mobile" })}</option>
                </select>
              </div>
              <div className="transactions-filter-field">
                <label>From Date</label>
                <input
                  type="date"
                  className="transactions-filter-input"
                  value={toDateInputValue(dateRange.dateFrom)}
                  onChange={(e) =>
                    setDateRange((p) => ({ ...p, dateFrom: parseLocalDateFromYmd(e.target.value) }))
                  }
                />
              </div>
              <div className="transactions-filter-field">
                <label>To Date</label>
                <input
                  type="date"
                  className="transactions-filter-input"
                  value={toDateInputValue(dateRange.dateTo)}
                  onChange={(e) =>
                    setDateRange((p) => ({ ...p, dateTo: parseLocalDateFromYmd(e.target.value) }))
                  }
                />
              </div>
              <div className="transactions-filter-actions">
                <button type="button" className="transactions-filter-apply" onClick={handleSearch}>
                  Filter
                </button>
                <button
                  type="button"
                  className="transactions-filter-reset"
                  onClick={() => {
                    setSearchTerm("");
                    setTypeFilter(TransactionType.ALL);
                    setDateRange({ dateFrom: null, dateTo: null, presetId: null });
                    setPage(0);
                    setTimeout(() => loadTransactions(), 0);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="accounts-table-container container-fluid px-0 d-flex flex-column">
          <div className="table-top-header">
            <div className="accounts-list-title-wrapper">
              <h1 className="accounts-list-title">{t("pending_transaction", { ns: "mobile" })}</h1>
            </div>
          </div>
          <div className="table-responsive accounts-table-scroll">
            <div className="accounts-table transactions-table pending-table">
            <div className="table-header-row">
              <div className="table-header-cell">{t("transaction_id", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("transaction_date", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("type", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("amount", { ns: "mobile" })}</div>
              <div className="table-header-cell">{t("action", { ns: "mobile" })}</div>
            </div>

            {isLoading && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{t("loading", { ns: "mobile" })}</div>
              </div>
            )}

            {!isLoading && error && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{error}</div>
              </div>
            )}

            {!isLoading && !error && transactions.length === 0 && (
              <div className="table-row no-data-row">
                <div className="table-cell no-data">{t("no_content", { ns: "mobile" })}</div>
              </div>
            )}

            {!isLoading &&
              !error &&
              transactions.map((tx) => (
                <div className="table-row" key={tx.id || `${tx.date}-${Math.random()}`}>
                  <div className="table-cell">{tx.id || "-"}</div>
                  <div className="table-cell">{formatDateTime(tx.date)}</div>
                  <div className="table-cell">
                    <span className={`type-badge ${tx.typeClass || "type-unknown"}`}>
                      {tx.typeLabel || "Transaction"}
                    </span>
                  </div>
                  <div className="table-cell">
                    {tx.amount != null
                      ? `${Number(tx.amount).toLocaleString()} ${resolveCurrencySymbol(tx)}`.trim()
                      : "-"}
                  </div>
                  <div className="table-cell">
                    <div className="request-action-buttons">
                      <button
                        className="request-action-btn request-action-btn--view"
                        aria-label="View details"
                        type="button"
                        onClick={() => openDetails(tx)}
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          </div>

          <div className="accounts-pagination accounts-pagination--opposite-dark">
            <div className="pagination-controls transactions-pagination-controls">
              <button
                className="pagination-btn"
                disabled={!canGoPrev}
                onClick={() => canGoPrev && setPage((prev) => Math.max(prev - 1, 0))}
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-right" : "fa-chevron-left"}`}></i>
              </button>
              <button className="pagination-btn active">
                <span>{page + 1}</span>
              </button>
              <button
                className="pagination-btn"
                disabled={!canGoNext}
                onClick={() => canGoNext && setPage((prev) => prev + 1)}
              >
                <i className={`fa-solid ${isRtl ? "fa-chevron-left" : "fa-chevron-right"}`}></i>
              </button>
            </div>
          </div>
        </div>
        <TransactionDetailsModal
          isOpen={isDetailsOpen}
          onClose={closeDetails}
          detailsTx={detailsTx}
          detailsData={detailsData}
          detailsLoading={detailsLoading}
          detailsError={detailsError}
          actions={
            canCancelTransaction(detailsTx, detailsData) ? (
              <button
                type="button"
                className="transactions-cancel-request-btn"
                disabled={cancelLoading}
                onClick={() => setShowDeletePopup(true)}
              >
                <i className="fa-solid fa-trash" />
                <span>{t("cancel_request", { ns: "mobile" })}</span>
              </button>
            ) : null
          }
        />
        <Popup
          isOpen={showDeletePopup && canCancelTransaction(detailsTx, detailsData)}
          onClose={() => setShowDeletePopup(false)}
          onConfirm={async () => {
            try {
              await handleCancelTransaction();
            } finally {
              setShowDeletePopup(false);
            }
          }}
          onCancel={() => setShowDeletePopup(false)}
          showCancel
          title={t("cancel_request", { ns: "mobile" })}
          message={t("cancel_cash_in_confirmation", { ns: "mobile" })}
          instruction={t("cancel_transaction_confirmation", { ns: "mobile" })}
          buttonText={t("confirm", { ns: "mobile" })}
          cancelButtonText={t("cancel", { ns: "mobile" })}
          icon="fa-trash"
        />
      </div>
    </div>
  );
};

export default PendingTransactions;
